import { useEffect, useState, type FormEvent } from "react";

export const accountStatusLabels: Record<string, string> = {
  draft: "Pending Activation",
  pending_activation: "Pending Activation",
  active: "Active",
  suspended: "Suspended",
  pending_disconnect: "Pending Disconnection",
  disconnected: "Disconnected",
};
export function accountStatusLabel(status: unknown) {
  return (
    accountStatusLabels[String(status)] ??
    String(status ?? "Not recorded").replaceAll("_", " ")
  );
}

export function AccountStatusPanel({
  account,
  api,
  csrf,
  onSaved,
}: {
  account: { id: string; status: string; version?: string | number };
  api: <T>(
    path: string,
    options?: { method?: string; body?: unknown; csrf?: string | undefined },
  ) => Promise<T>;
  csrf?: string | undefined;
  onSaved: () => void | Promise<void>;
}) {
  const [status, setStatus] = useState(
    account.status === "draft" ? "pending_activation" : account.status,
  );
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(
    () =>
      setStatus(
        account.status === "draft" ? "pending_activation" : account.status,
      ),
    [account.status, account.version],
  );
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api(`/admin/accounts/${account.id}/status`, {
        method: "POST",
        csrf,
        body: {
          status,
          reason,
          expectedVersion: Number(account.version),
          recordOnlyAcknowledged: acknowledged,
        },
      });
      setSaved(true);
      setReason("");
      setAcknowledged(false);
      await onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="cr-panel cr-editor">
      <summary>Change account status</summary>
      <p>
        Pending Activation means the account is recorded and setup is not yet
        marked complete. Account status is a staff-maintained record, separate
        from live phone status.
      </p>
      <form className="cr-form" onSubmit={(event) => void save(event)}>
        <label className="cr-field">
          <span>Account status</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setSaved(false);
            }}
            disabled={busy}
          >
            {Object.entries(accountStatusLabels)
              .filter(([value]) => value !== "draft")
              .map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
          </select>
        </label>
        <label className="cr-field">
          <span>Reason for change</span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            minLength={5}
            maxLength={500}
            disabled={busy}
          />
        </label>
        <label className="cr-check">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            required
            disabled={busy}
          />{" "}
          I understand this updates the account record only. It does not start
          or stop calling, change billing, or verify emergency service.
        </label>
        <button
          className="cr-button"
          disabled={busy || !account.version || !acknowledged}
        >
          {busy ? "Saving…" : "Save account status"}
        </button>
        {error && <p role="alert">{error}</p>}
        {saved && (
          <p role="status">
            Account status saved. The change is recorded in account history.
          </p>
        )}
      </form>
    </details>
  );
}
